import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import RefreshIcon from "@material-ui/icons/Refresh";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SettingsIcon from "@material-ui/icons/Settings";
import PeopleIcon from "@material-ui/icons/People";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n.js";
import BulkSendProfilesEditor from "../../pages/Companies/BulkSendProfilesEditor";

// feat_232: operationStatus colors for the General edit form
const OP_STATUS_META = {
  active: "#22c55e",
  paused: "#9ca3af",
  blocked: "#ef4444",
  waiting_meta: "#f59e0b",
  demo: "#3b82f6",
  trial: "#38bdf8",
};
const OP_STATUS_KEYS = ["active", "paused", "blocked", "waiting_meta", "demo", "trial"];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  paper: { overflowX: "auto" },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  fieldInput: { flex: 1 },
  sectionTitle: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
}));

const EMPTY_COMPANY = { name: "", slug: "", plan: "basic", active: true, instanceId: "", operationStatus: "active", notes: "" };
const EMPTY_ADMIN = { name: "", email: "", password: "" };
const EMPTY_USER = { name: "", email: "", password: "", profile: "admin" };
const WEBHOOK_FIELDS = [
  { key: "webhookUrl", label: "URL de Automatización" },
  { key: "webhookToken", label: "Token de Automatización" },
  { key: "webhookTimeout", label: "Timeout (segundos)" },
  { key: "webhookRetries", label: "Reintentos" },
  { key: "webhookPrompt", label: "Prompt del Asistente", multiline: true },
  { key: "userApiToken", label: "API Token", readOnly: true },
];

const WEBHOOK_METHOD_OPTIONS = ["POST", "GET"];

const CompanyEditModal = ({ open, company, initialTab = "general", onClose, onSaved }) => {
  const classes = useStyles();
  const [instances, setInstances] = useState([]);

  // Company CRUD modal
  const [companyModal, setCompanyModal] = useState({ open: false, data: null });
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN);

  // Settings modal
  const [settingsModal, setSettingsModal] = useState({ open: false, companyId: null, companyName: "", company: null });
  const [settingsForm, setSettingsForm] = useState({});
  const [visibleFields, setVisibleFields] = useState([]);
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const [webhookTestLoading, setWebhookTestLoading] = useState(false);
  const [companyStats, setCompanyStats] = useState({ userCount: 0, whatsappCount: 0, connectedCount: 0, totalContacts: 0, totalTickets: 0, activeTickets: 0, resolvedToday: 0, totalMessages: 0, whatsappConnections: [] });
  const [limitsForm, setLimitsForm] = useState({ maxWhatsapps: "", maxUsers: "", historyDays: "", closeOnGained: true, closeOnLost: true, secretariaIaEnabled: true, secretariaIaSuperadminEnabled: true, vendedorIaSuperadminEnabled: true, iaAdminCanToggle: false, emailReportSuperadminAllowed: false, ticketReopenWindowDays: "", language: "es" });
  const [settingsTab, setSettingsTab] = useState(0);
  const [dataForms, setDataForms] = useState([
    { slot: 1, title: "", url: "", enabled: false, slotType: "iframe", profileId: null },
    { slot: 2, title: "", url: "", enabled: false, slotType: "iframe", profileId: null },
    { slot: 3, title: "", url: "", enabled: false, slotType: "iframe", profileId: null },
    { slot: 4, title: "", url: "", enabled: false, slotType: "iframe", profileId: null },
  ]);
  const [dataFormSaving, setDataFormSaving] = useState({});
  // feat_210 Fase D — profiles activos, para el selector del slot bulk_send.
  const [bulkSendProfilesForSlot, setBulkSendProfilesForSlot] = useState([]);

  // Hard delete state
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState("");
  const [hardDeleting, setHardDeleting] = useState(false);

  // Users modal
  const [usersModal, setUsersModal] = useState({ open: false, companyId: null, companyName: "" });
  const [users, setUsers] = useState([]);
  const [newUserForm, setNewUserForm] = useState(EMPTY_USER);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [editUserModal, setEditUserModal] = useState({ open: false, user: null });
  const [editUserForm, setEditUserForm] = useState({ name: "", email: "", password: "", profile: "admin" });

  const notifySaved = useCallback(() => { if (onSaved) onSaved(); }, [onSaved]);

  // Close every internal sub-modal and notify the parent to lower `open`.
  const handleClose = () => {
    setCompanyModal({ open: false, data: null });
    setSettingsModal({ open: false, companyId: null, companyName: "", company: null });
    setUsersModal({ open: false, companyId: null, companyName: "" });
    setEditUserModal({ open: false, user: null });
    setHardDeleteTarget(null);
    if (onClose) onClose();
  };

  useEffect(() => {
    api.get("/superadmin/whitelabel/instances")
      .then(r => setInstances(r.data || []))
      .catch(() => {});
  }, []);

  // Open the right sub-modal when the parent requests it (feat_232).
  useEffect(() => {
    if (!open) return;
    if (!company) { openCreate(); return; }
    if (initialTab === "users") openUsers(company);
    else if (initialTab === "settings") openSettings(company);
    else openEdit(company);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company, initialTab]);

  // ── Company modal ──────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_COMPANY);
    setAdminForm(EMPTY_ADMIN);
    setCompanyModal({ open: true, data: null });
  };

  const openEdit = (company) => {
    setForm({ name: company.name, slug: company.slug, plan: company.plan, active: company.active, instanceId: company.instanceId || "", operationStatus: company.operationStatus || "active", notes: company.notes || "" });
    setAdminForm(EMPTY_ADMIN);
    setCompanyModal({ open: true, data: company });
  };

  const handleSaveCompany = async () => {
    try {
      let companyId;
      if (companyModal.data) {
        await api.put(`/companies/${companyModal.data.id}`, form);
        toast.success("Empresa actualizada");
        companyId = companyModal.data.id;
      } else {
        const { data: created } = await api.post("/companies", form);
        toast.success("Empresa creada");
        companyId = created.id;

        // Create initial admin if provided
        if (adminForm.name && adminForm.email && adminForm.password) {
          try {
            await api.post(`/companies/${companyId}/users`, {
              name: adminForm.name,
              email: adminForm.email,
              password: adminForm.password,
              profile: "admin"
            });
            toast.success("Usuario administrador creado");
          } catch (err) {
            toastError(err);
          }
        }
      }
      notifySaved();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeactivate = async (company) => {
    if (!window.confirm(`¿Desactivar "${company.name}"?`)) return;
    try {
      await api.delete(`/companies/${company.id}`);
      toast.success("Empresa desactivada");
      notifySaved();
    } catch (err) {
      toastError(err);
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget || hardDeleteConfirm !== hardDeleteTarget.name) return;
    setHardDeleting(true);
    try {
      await api.delete(`/companies/${hardDeleteTarget.id}/hard`);
      toast.success(`Empresa "${hardDeleteTarget.name}" eliminada permanentemente`);
      setHardDeleteTarget(null);
      setHardDeleteConfirm("");
      notifySaved();
    } catch (err) {
      toastError(err);
    }
    setHardDeleting(false);
  };

  const handleRegenerateToken = async (companyId) => {
    if (!window.confirm("¿Regenerar el token de API? El token actual dejará de funcionar inmediatamente.")) return;
    try {
      const { data } = await api.post(`/companies/${companyId}/regenerate-token`);
      setSettingsForm(prev => ({ ...prev, userApiToken: data.token }));
      toast.success("Token regenerado");
    } catch (err) {
      toastError(err);
    }
  };

  const handleTestWebhook = async (companyId) => {
    setWebhookTestLoading(true);
    setWebhookTestResult(null);
    try {
      const { data } = await api.get(`/companies/${companyId}/webhook-test`);
      setWebhookTestResult(data);
      // Refresh lastWebhookTest / lastWebhookStatus in form
      const { data: updated } = await api.get(`/companies/${companyId}/settings`);
      const map = {};
      updated.forEach((s) => { map[s.key] = s.value; });
      setSettingsForm(prev => ({
        ...prev,
        lastWebhookTest: map.lastWebhookTest,
        lastWebhookStatus: map.lastWebhookStatus,
      }));
    } catch (err) {
      setWebhookTestResult({ ok: false, error: "Error de red", responseTime: 0 });
    }
    setWebhookTestLoading(false);
  };

  // ── Settings modal ─────────────────────────────────────────
  const openSettings = async (company) => {
    try {
      const [{ data }, { data: stats }] = await Promise.all([
        api.get(`/companies/${company.id}/settings`),
        api.get(`/companies/${company.id}/stats`),
      ]);
      const map = {};
      data.forEach((s) => { map[s.key] = s.value; });
      let visible = [];
      try { visible = JSON.parse(map.clientVisibleFields || "[]"); } catch (_) {}
      setSettingsForm(map);
      setVisibleFields(visible);
      setCompanyStats(stats);
      setWebhookTestResult(null);
      setSettingsTab(0);
      setLimitsForm({
        maxWhatsapps: company.maxWhatsapps ?? "",
        maxUsers: company.maxUsers ?? "",
        historyDays: company.historyDays ?? "",
        closeOnGained: company.closeOnGained !== false,
        closeOnLost: company.closeOnLost !== false,
        secretariaIaEnabled: map["secretariaIaEnabled"] !== "false",
        secretariaIaSuperadminEnabled: map["secretariaIaSuperadminEnabled"] !== "false",
        vendedorIaSuperadminEnabled: map["vendedorIaSuperadminEnabled"] !== "false",
        iaAdminCanToggle: map["iaAdminCanToggle"] === "true",
        emailReportSuperadminAllowed: map["emailReportSuperadminAllowed"] === "true",
        ticketReopenWindowDays: map["ticketReopenWindowDays"] ?? "",
        language: map["language"] ?? "es",
      });
      setSettingsModal({ open: true, companyId: company.id, companyName: company.name, company });

      // Load data forms for tab 4
      api.get(`/superadmin/company-data-forms/${company.id}`)
        .then(({ data }) => {
          setDataForms(data.length === 4 ? data : [1, 2, 3, 4].map(slot => {
            const found = data.find(d => d.slot === slot);
            return found || { slot, title: "", url: "", enabled: false, slotType: "iframe", profileId: null };
          }));
        })
        .catch(() => {});

      // feat_210 Fase D — profiles activos para el selector del slot bulk_send.
      api.get(`/superadmin/companies/${company.id}/bulk-send-profiles`)
        .then(({ data }) => {
          setBulkSendProfilesForSlot(Array.isArray(data) ? data.filter(p => p.active) : []);
        })
        .catch(() => setBulkSendProfilesForSlot([]));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const settings = Object.entries(settingsForm).map(([key, value]) => ({ key, value: String(value) }));
      const existingIdx = settings.findIndex((s) => s.key === "clientVisibleFields");
      const visibleJson = JSON.stringify(visibleFields);
      if (existingIdx >= 0) {
        settings[existingIdx].value = visibleJson;
      } else {
        settings.push({ key: "clientVisibleFields", value: visibleJson });
      }
      settings.push({ key: "secretariaIaEnabled", value: limitsForm.secretariaIaEnabled ? "true" : "false" });
      settings.push({ key: "secretariaIaSuperadminEnabled", value: limitsForm.secretariaIaSuperadminEnabled ? "true" : "false" });
      settings.push({ key: "vendedorIaSuperadminEnabled", value: limitsForm.vendedorIaSuperadminEnabled ? "true" : "false" });
      settings.push({ key: "iaAdminCanToggle", value: limitsForm.iaAdminCanToggle ? "true" : "false" });
      settings.push({ key: "emailReportSuperadminAllowed", value: limitsForm.emailReportSuperadminAllowed ? "true" : "false" });
      settings.push({ key: "ticketReopenWindowDays", value: limitsForm.ticketReopenWindowDays === "" ? "1" : String(limitsForm.ticketReopenWindowDays) });
      settings.push({ key: "language", value: limitsForm.language || "es" });
      await api.put(`/companies/${settingsModal.companyId}/settings`, { settings });
      await api.put(`/companies/${settingsModal.companyId}`, {
        name: settingsModal.company.name,
        slug: settingsModal.company.slug,
        plan: settingsModal.company.plan,
        active: settingsModal.company.active,
        maxWhatsapps: limitsForm.maxWhatsapps === "" ? null : Number(limitsForm.maxWhatsapps),
        maxUsers: limitsForm.maxUsers === "" ? null : Number(limitsForm.maxUsers),
        historyDays: limitsForm.historyDays === "" ? null : Number(limitsForm.historyDays),
        closeOnGained: limitsForm.closeOnGained,
        closeOnLost: limitsForm.closeOnLost,
      });
      toast.success("Configuración guardada");
      notifySaved();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const toggleVisible = (key) => {
    setVisibleFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSaveDataFormSlot = async (slot) => {
    const form = dataForms.find(f => f.slot === slot);
    if (!form) return;
    if (!form.title || form.title.trim() === "") {
      toast.error("El título es obligatorio");
      return;
    }
    const slotType = form.slotType === "bulk_send" ? "bulk_send" : "iframe";
    if (slotType === "iframe") {
      if (!form.url || !form.url.startsWith("https://")) {
        toast.error("La URL debe comenzar con https://");
        return;
      }
    } else if (!form.profileId) {
      toast.error("Elegí un profile de envío masivo");
      return;
    }
    setDataFormSaving(prev => ({ ...prev, [slot]: true }));
    try {
      // feat_210: mandar slotType/profileId SIEMPRE explícitos desde este
      // formulario (nunca omitirlos) — el backend con el fix de preserve los
      // mantiene si se omiten, pero acá el operador está editando este campo
      // a propósito, tiene que quedar editable de verdad.
      await api.put(`/superadmin/company-data-forms/${settingsModal.companyId}/${slot}`, {
        title: form.title,
        url: slotType === "iframe" ? form.url : "",
        enabled: form.enabled,
        slotType,
        profileId: slotType === "bulk_send" ? Number(form.profileId) : null,
      });
      toast.success(`Slot ${slot} guardado`);
    } catch (err) {
      toastError(err);
    }
    setDataFormSaving(prev => ({ ...prev, [slot]: false }));
  };

  const updateDataFormSlot = (slot, field, value) => {
    setDataForms(prev => prev.map(f => f.slot === slot ? { ...f, [field]: value } : f));
  };

  // ── Users modal ────────────────────────────────────────────
  const openUsers = async (company) => {
    try {
      const { data } = await api.get(`/companies/${company.id}/users`);
      setUsers(data);
      setNewUserForm(EMPTY_USER);
      setShowNewUserForm(false);
      setUsersModal({ open: true, companyId: company.id, companyName: company.name });
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateUser = async () => {
    try {
      await api.post(`/companies/${usersModal.companyId}/users`, newUserForm);
      toast.success("Usuario creado");
      const { data } = await api.get(`/companies/${usersModal.companyId}/users`);
      setUsers(data);
      setNewUserForm(EMPTY_USER);
      setShowNewUserForm(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("¿Desactivar este usuario?")) return;
    try {
      await api.delete(`/companies/${usersModal.companyId}/users/${userId}`);
      toast.success("Usuario desactivado");
      const { data } = await api.get(`/companies/${usersModal.companyId}/users`);
      setUsers(data);
    } catch (err) {
      toastError(err);
    }
  };

  const openEditUser = (user) => {
    setEditUserForm({ name: user.name, email: user.email, password: "", profile: user.profile });
    setEditUserModal({ open: true, user });
  };

  const handleEditUser = async () => {
    try {
      const payload = { name: editUserForm.name, email: editUserForm.email, profile: editUserForm.profile };
      if (editUserForm.password) payload.password = editUserForm.password;
      await api.put(`/companies/${usersModal.companyId}/users/${editUserModal.user.id}`, payload);
      toast.success("Usuario actualizado");
      setEditUserModal({ open: false, user: null });
      const { data } = await api.get(`/companies/${usersModal.companyId}/users`);
      setUsers(data);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      {/* ── Create/Edit Modal ── */}
      <Dialog open={companyModal.open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{companyModal.data ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Slug (ej: del-metal)"
            fullWidth
            margin="dense"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
          />
          <Select
            fullWidth
            value={form.plan}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
            style={{ marginTop: 8 }}
          >
            <MenuItem value="basic">Basic</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
            <MenuItem value="full">Full</MenuItem>
          </Select>
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                color="primary"
              />
            }
            label="Activa"
          />
          <Select
            fullWidth
            displayEmpty
            value={form.instanceId}
            onChange={(e) => setForm({ ...form, instanceId: e.target.value })}
            style={{ marginTop: 8 }}
          >
            <MenuItem value=""><em>WAIA (plataforma por defecto)</em></MenuItem>
            {instances.map(inst => (
              <MenuItem key={inst.id} value={inst.id}>{inst.name} ({inst.domain})</MenuItem>
            ))}
          </Select>

          {/* feat_232: operationStatus (informativo) + notes */}
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 12, display: "block" }}>
            {i18n.t("panel.col.status")}
          </Typography>
          <Select
            fullWidth
            value={form.operationStatus}
            onChange={(e) => setForm({ ...form, operationStatus: e.target.value })}
            renderValue={(v) => (
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: OP_STATUS_META[v], marginRight: 8 }} />
                {i18n.t(`operationStatus.${v}`)}
              </span>
            )}
          >
            {OP_STATUS_KEYS.map((s) => (
              <MenuItem key={s} value={s}>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: OP_STATUS_META[s], marginRight: 8 }} />
                {i18n.t(`operationStatus.${s}`)}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label={i18n.t("panel.notesLabel")}
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />

          {/* Initial admin — only on create */}
          {!companyModal.data && (
            <>
              <Divider style={{ margin: "16px 0 8px" }} />
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                Usuario administrador inicial (opcional)
              </Typography>
              <TextField
                label="Nombre"
                fullWidth
                margin="dense"
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
              />
              <TextField
                label="Email"
                fullWidth
                margin="dense"
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
              />
              <TextField
                label="Contraseña"
                fullWidth
                margin="dense"
                type="password"
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSaveCompany} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Users Modal ── */}
      <Dialog
        open={usersModal.open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Usuarios — {usersModal.companyName}
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Perfil</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">Sin usuarios</Typography>
                  </TableCell>
                </TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.profile === "admin" ? "Admin" : "Asesor"}
                      size="small"
                      color={u.profile === "admin" ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEditUser(u)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Desactivar">
                      <IconButton size="small" onClick={() => handleDeactivateUser(u.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider style={{ margin: "16px 0 8px" }} />

          {!showNewUserForm ? (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setShowNewUserForm(true)}
            >
              Nuevo usuario
            </Button>
          ) : (
            <>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Nuevo usuario</Typography>
              <TextField
                label="Nombre"
                fullWidth
                margin="dense"
                value={newUserForm.name}
                onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
              />
              <TextField
                label="Email"
                fullWidth
                margin="dense"
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
              />
              <TextField
                label="Contraseña"
                fullWidth
                margin="dense"
                type="password"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
              />
              <Select
                fullWidth
                value={newUserForm.profile}
                onChange={(e) => setNewUserForm({ ...newUserForm, profile: e.target.value })}
                style={{ marginTop: 8 }}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">Asesor</MenuItem>
              </Select>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Button variant="contained" color="primary" size="small" onClick={handleCreateUser}>
                  Crear usuario
                </Button>
                <Button size="small" onClick={() => { setShowNewUserForm(false); setNewUserForm(EMPTY_USER); }}>
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit User Modal ── */}
      <Dialog open={editUserModal.open} onClose={() => setEditUserModal({ open: false, user: null })} fullWidth maxWidth="xs">
        <DialogTitle>Editar usuario</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            margin="dense"
            value={editUserForm.name}
            onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            margin="dense"
            type="email"
            value={editUserForm.email}
            onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
          />
          <TextField
            label="Nueva contraseña (dejar vacío para no cambiar)"
            fullWidth
            margin="dense"
            type="password"
            value={editUserForm.password}
            onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
          />
          <Select
            fullWidth
            value={editUserForm.profile}
            onChange={(e) => setEditUserForm({ ...editUserForm, profile: e.target.value })}
            style={{ marginTop: 8 }}
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">Asesor</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserModal({ open: false, user: null })}>Cancelar</Button>
          <Button onClick={handleEditUser} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Settings Modal ── */}
      <Dialog
        open={settingsModal.open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { width: "min(900px, 90vw)", maxWidth: "none" } }}
      >
        <DialogTitle>Configuración — {settingsModal.companyName}</DialogTitle>
        <DialogContent style={{ padding: "0 24px 16px" }}>
          <Tabs
            value={settingsTab}
            onChange={(_, v) => setSettingsTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="General" />
            <Tab label="Módulos" />
            <Tab label="Automatización" />
            <Tab label="Límites" />
            <Tab label="Carga de Datos" />
            <Tab label="Envío Masivo" />
          </Tabs>
          <Divider />

          {/* ── Tab 0: General ── */}
          {settingsTab === 0 && (
            <div style={{ paddingTop: 16 }}>
              {(() => {
                const c = settingsModal.company;
                if (!c) return null;
                let label = "Activa";
                let color = "#10b981";
                if (c.suspendedAt) { label = "Suspendida"; color = "#ef4444"; }
                else if (c.trialEndsAt && new Date(c.trialEndsAt) > new Date()) { label = "Trial"; color = "#f59e0b"; }
                else if (!c.active) { label = "Inactiva"; color = "#6b7280"; }
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    <Chip label={label} size="small" style={{ backgroundColor: color, color: "#fff", fontWeight: 600 }} />
                    <Chip label={`Plan: ${c.plan}`} size="small" variant="outlined" />
                    <Chip label={`ID: ${c.id}`} size="small" variant="outlined" />
                  </div>
                );
              })()}

              <Typography variant="body2" style={{ marginBottom: 4 }}>
                <strong>Creado:</strong>{" "}
                {settingsModal.company
                  ? new Date(settingsModal.company.createdAt).toLocaleDateString("es-AR")
                  : "—"}
              </Typography>
              {settingsModal.company?.suspendedAt && (
                <Typography variant="body2" style={{ color: "#ef4444", marginBottom: 4 }}>
                  <strong>Suspendido:</strong>{" "}
                  {new Date(settingsModal.company.suspendedAt).toLocaleString("es-AR")}
                  {settingsModal.company.suspendedReason && ` — ${settingsModal.company.suspendedReason}`}
                </Typography>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8, marginBottom: 16 }}>
                <div style={{ textAlign: "center", padding: "8px 14px", background: "#f3f4f6", borderRadius: 8 }}>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>{companyStats.userCount}</Typography>
                  <Typography variant="caption" color="textSecondary">Usuarios</Typography>
                </div>
                <div style={{ textAlign: "center", padding: "8px 14px", background: "#f3f4f6", borderRadius: 8 }}>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1, color: companyStats.connectedCount > 0 ? "#10b981" : "#6b7280" }}>
                    {companyStats.connectedCount}/{companyStats.whatsappCount}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">WA conectados</Typography>
                </div>
                <div style={{ textAlign: "center", padding: "8px 14px", background: "#f3f4f6", borderRadius: 8 }}>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>{companyStats.totalContacts}</Typography>
                  <Typography variant="caption" color="textSecondary">Contactos</Typography>
                </div>
                <div style={{ textAlign: "center", padding: "8px 14px", background: "#f3f4f6", borderRadius: 8 }}>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1, color: companyStats.activeTickets > 0 ? "#f59e0b" : "#6b7280" }}>
                    {companyStats.activeTickets}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Tickets activos</Typography>
                </div>
                <div style={{ textAlign: "center", padding: "8px 14px", background: "#f3f4f6", borderRadius: 8 }}>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1, color: "#10b981" }}>{companyStats.resolvedToday}</Typography>
                  <Typography variant="caption" color="textSecondary">Cerrados hoy</Typography>
                </div>
                <div style={{ textAlign: "center", padding: "8px 14px", background: "#f3f4f6", borderRadius: 8 }}>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>{companyStats.totalMessages}</Typography>
                  <Typography variant="caption" color="textSecondary">Mensajes</Typography>
                </div>
              </div>

              {companyStats.whatsappConnections && companyStats.whatsappConnections.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>Conexiones WhatsApp:</Typography>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {companyStats.whatsappConnections.map((w) => (
                      <Chip
                        key={w.id}
                        label={`${w.name}${w.isDefault ? " ★" : ""} — ${w.status}`}
                        size="small"
                        style={{
                          backgroundColor: w.status === "CONNECTED" ? "#d1fae5" : "#fee2e2",
                          color: w.status === "CONNECTED" ? "#065f46" : "#991b1b",
                          fontWeight: 600,
                          fontSize: 11
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <Divider style={{ marginBottom: 12 }} />

              {/* Tipos de conexión habilitados */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>
                Tipos de conexión habilitados
              </Typography>
              {(() => {
                let allowed = ["whaileys"];
                try { allowed = JSON.parse(settingsForm["allowedConnectionTypes"] || '["whaileys"]'); } catch (_) {}
                const toggle = (type) => {
                  let next = [...allowed];
                  if (next.includes(type)) {
                    next = next.filter(t => t !== type);
                  } else {
                    next.push(type);
                  }
                  setSettingsForm(prev => ({ ...prev, allowedConnectionTypes: JSON.stringify(next) }));
                };
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={allowed.includes("whaileys")}
                          onChange={() => toggle("whaileys")}
                        />
                      }
                      label={<span style={{ fontSize: 13 }}>WhatsApp QR — Pro</span>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={allowed.includes("wwebjs")}
                          onChange={() => toggle("wwebjs")}
                        />
                      }
                      label={<span style={{ fontSize: 13 }}>WhatsApp QR — Web</span>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={allowed.includes("meta")}
                          onChange={() => toggle("meta")}
                        />
                      }
                      label={<span style={{ fontSize: 13 }}>Meta Cloud API</span>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={allowed.includes("meta_coexistence")}
                          onChange={() => toggle("meta_coexistence")}
                        />
                      }
                      label={<span style={{ fontSize: 13 }}>Meta Coexistence</span>}
                    />
                  </div>
                );
              })()}

              <Divider style={{ marginBottom: 16 }} />

              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>API Token</Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <TextField
                  label="Token"
                  fullWidth
                  margin="dense"
                  value={settingsForm["userApiToken"] || ""}
                  InputProps={{ readOnly: true, style: { fontFamily: "monospace", fontSize: 12 } }}
                />
                <Tooltip title="Copiar token">
                  <IconButton size="small" onClick={() => {
                    const val = settingsForm["userApiToken"] || "";
                    if (val) navigator.clipboard.writeText(val).then(() => toast.success("Token copiado")).catch(() => {});
                  }}>
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Regenerar token">
                  <IconButton size="small" onClick={() => handleRegenerateToken(settingsModal.companyId)}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </div>

              <Divider style={{ margin: "16px 0 8px" }} />

              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>Actividad de webhook</Typography>
              {settingsForm["lastWebhookTest"] ? (
                <div style={{ background: "#f9fafb", borderRadius: 6, padding: 10 }}>
                  <Typography variant="caption" display="block">
                    <strong>Último test:</strong>{" "}
                    {new Date(settingsForm["lastWebhookTest"]).toLocaleString("es-AR")}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Status:</strong>{" "}
                    <span style={{ color: settingsForm["lastWebhookStatus"] === "error" || Number(settingsForm["lastWebhookStatus"]) >= 400 ? "#ef4444" : "#10b981", fontWeight: 600 }}>
                      {settingsForm["lastWebhookStatus"] || "—"}
                    </span>
                  </Typography>
                </div>
              ) : (
                <Typography variant="caption" color="textSecondary">Sin historial de tests</Typography>
              )}
            </div>
          )}

          {/* ── Tab 1: Módulos ── */}
          {settingsTab === 1 && (
            <div style={{ paddingTop: 16 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>Integración IA</Typography>

              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={settingsForm["iaEnabled"] === "true"}
                    onChange={(e) => setSettingsForm({ ...settingsForm, iaEnabled: e.target.checked ? "true" : "false" })}
                  />
                }
                label="IA habilitada"
              />

              <FormControlLabel
                style={{ display: "block", marginTop: 4 }}
                control={
                  <Switch
                    color="primary"
                    checked={settingsForm["appointmentsEnabled"] === "true"}
                    onChange={(e) => setSettingsForm({ ...settingsForm, appointmentsEnabled: e.target.checked ? "true" : "false" })}
                  />
                }
                label="Módulo de Citas"
              />

              <FormControlLabel
                style={{ display: "block", marginTop: 4 }}
                control={
                  <Switch
                    color="primary"
                    checked={settingsForm["turnosIAEnabled"] === "true"}
                    onChange={(e) => setSettingsForm({ ...settingsForm, turnosIAEnabled: e.target.checked ? "true" : "false" })}
                  />
                }
                label="TurnosIA — Gestión de turnos con IA"
              />

              {/* feat_333 — modo WAIAenvios: PODA el panel a Conexiones + Carga de
                  Datos para todo perfil no-superadmin de la empresa. Ausente = "false". */}
              <FormControlLabel
                style={{ display: "block", marginTop: 4 }}
                control={
                  <Switch
                    color="primary"
                    checked={settingsForm["waiaEnviosMode"] === "true"}
                    onChange={(e) => setSettingsForm({ ...settingsForm, waiaEnviosMode: e.target.checked ? "true" : "false" })}
                  />
                }
                label={i18n.t("waiaEnvios.switchLabel")}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginLeft: 46, marginTop: -4 }}>
                {i18n.t("waiaEnvios.switchHelp")}
              </Typography>

              <TextField
                label="Token OpenAI"
                fullWidth
                margin="dense"
                type="password"
                autoComplete="new-password"
                value={settingsForm["openaiToken"] || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, openaiToken: e.target.value })}
                style={{ marginTop: 12 }}
              />

              <TextField
                label="Prompt IA"
                fullWidth
                margin="dense"
                multiline
                rows={6}
                value={settingsForm["webhookPrompt"] || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, webhookPrompt: e.target.value })}
                style={{ marginTop: 8 }}
                helperText={
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    Visible para el cliente{" "}
                    <Switch
                      size="small"
                      color="primary"
                      checked={visibleFields.includes("webhookPrompt")}
                      onChange={() => toggleVisible("webhookPrompt")}
                    />
                  </span>
                }
              />
            </div>
          )}

          {/* ── Tab 2: Automatización ── */}
          {settingsTab === 2 && (
            <div style={{ paddingTop: 16 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>Webhook / API</Typography>

              <TextField
                label="URL de Automatización"
                fullWidth
                margin="dense"
                value={settingsForm["webhookUrl"] || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, webhookUrl: e.target.value })}
                helperText={
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    Visible para el cliente{" "}
                    <Switch
                      size="small"
                      color="primary"
                      checked={visibleFields.includes("webhookUrl")}
                      onChange={() => toggleVisible("webhookUrl")}
                    />
                  </span>
                }
              />

              <TextField
                label="Token de Automatización"
                fullWidth
                margin="dense"
                value={settingsForm["webhookToken"] || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, webhookToken: e.target.value })}
                helperText={
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    Visible para el cliente{" "}
                    <Switch
                      size="small"
                      color="primary"
                      checked={visibleFields.includes("webhookToken")}
                      onChange={() => toggleVisible("webhookToken")}
                    />
                  </span>
                }
              />

              <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginTop: 4 }}>
                <TextField
                  label="Timeout (seg)"
                  margin="dense"
                  type="number"
                  value={settingsForm["webhookTimeout"] || "10"}
                  onChange={(e) => setSettingsForm({ ...settingsForm, webhookTimeout: e.target.value })}
                  style={{ width: 120 }}
                  inputProps={{ min: 1, max: 120 }}
                  helperText={
                    <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                      Visible{" "}<Switch size="small" color="primary" checked={visibleFields.includes("webhookTimeout")} onChange={() => toggleVisible("webhookTimeout")} />
                    </span>
                  }
                />
                <TextField
                  label="Reintentos"
                  margin="dense"
                  type="number"
                  value={settingsForm["webhookRetries"] || "2"}
                  onChange={(e) => setSettingsForm({ ...settingsForm, webhookRetries: e.target.value })}
                  style={{ width: 120 }}
                  inputProps={{ min: 0, max: 10 }}
                  helperText={
                    <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                      Visible{" "}<Switch size="small" color="primary" checked={visibleFields.includes("webhookRetries")} onChange={() => toggleVisible("webhookRetries")} />
                    </span>
                  }
                />
                <div>
                  <Typography variant="caption" color="textSecondary">Método</Typography>
                  <Select
                    value={settingsForm["webhookMethod"] || "POST"}
                    onChange={(e) => setSettingsForm({ ...settingsForm, webhookMethod: e.target.value })}
                    style={{ display: "block", minWidth: 80 }}
                  >
                    {WEBHOOK_METHOD_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </div>
              </div>

              <FormControlLabel
                style={{ marginTop: 8 }}
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={settingsForm["webhookAuthEnabled"] !== "false"}
                    onChange={(e) => setSettingsForm({ ...settingsForm, webhookAuthEnabled: e.target.checked ? "true" : "false" })}
                  />
                }
                label={<Typography variant="body2">Autenticación habilitada</Typography>}
              />

              <div style={{ marginTop: 16 }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  disabled={webhookTestLoading || !settingsForm["webhookUrl"]}
                  onClick={() => handleTestWebhook(settingsModal.companyId)}
                  style={{ minWidth: 160 }}
                >
                  {webhookTestLoading
                    ? <><CircularProgress size={14} style={{ marginRight: 6 }} />Probando...</>
                    : "▶ Probar webhook"}
                </Button>

                {webhookTestResult && (
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: 8,
                      padding: "6px 10px",
                      borderRadius: 4,
                      background: webhookTestResult.ok ? "#d1fae5" : "#fee2e2",
                      color: webhookTestResult.ok ? "#065f46" : "#991b1b",
                      fontWeight: 600,
                    }}
                  >
                    {webhookTestResult.ok
                      ? `✅ OK — HTTP ${webhookTestResult.status} (${webhookTestResult.responseTime}ms)`
                      : webhookTestResult.status === 403
                        ? `⚠️ 403 — n8n recibió el webhook pero el flujo está inactivo (${webhookTestResult.responseTime}ms)`
                        : webhookTestResult.error === "Timeout (10s)"
                          ? `⏱ Timeout — el servidor no respondió en 10s`
                          : webhookTestResult.error && webhookTestResult.status === null
                            ? `🔌 Error de red — ${webhookTestResult.error}`
                            : `❌ HTTP ${webhookTestResult.status} — ${webhookTestResult.error || "error"} (${webhookTestResult.responseTime}ms)`}
                  </Typography>
                )}
              </div>

              <Divider style={{ margin: "16px 0 8px" }} />
              <Typography variant="caption" color="textSecondary">
                Los campos marcados como "Visible" son accesibles y editables por el administrador de la empresa.
              </Typography>
            </div>
          )}

          {/* ── Tab 3: Límites ── */}
          {settingsTab === 3 && (
            <div style={{ paddingTop: 16 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12 }}>Límites de recursos</Typography>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <TextField
                  label="Máx. WhatsApp"
                  margin="dense"
                  type="number"
                  value={limitsForm.maxWhatsapps}
                  onChange={(e) => setLimitsForm({ ...limitsForm, maxWhatsapps: e.target.value })}
                  style={{ width: 140 }}
                  inputProps={{ min: 1 }}
                  helperText="Vacío = ilimitado (por plan)"
                />
                <TextField
                  label="Máx. usuarios"
                  margin="dense"
                  type="number"
                  value={limitsForm.maxUsers}
                  onChange={(e) => setLimitsForm({ ...limitsForm, maxUsers: e.target.value })}
                  style={{ width: 140 }}
                  inputProps={{ min: 1 }}
                  helperText="Vacío = ilimitado (por plan)"
                />
                <TextField
                  label="Días de historial"
                  margin="dense"
                  type="number"
                  value={limitsForm.historyDays}
                  onChange={(e) => setLimitsForm({ ...limitsForm, historyDays: e.target.value })}
                  style={{ width: 140 }}
                  inputProps={{ min: 1 }}
                  helperText="Vacío = sin límite"
                />
              </div>

              <Divider style={{ margin: "16px 0 8px" }} />
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>Comportamiento al cerrar oportunidad</Typography>

              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.closeOnGained}
                    onChange={(e) => setLimitsForm({ ...limitsForm, closeOnGained: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Cerrar ticket al marcar como Ganado</Typography>}
              />
              <FormControlLabel
                style={{ display: "block", marginTop: 4 }}
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.closeOnLost}
                    onChange={(e) => setLimitsForm({ ...limitsForm, closeOnLost: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Cerrar ticket al marcar como Perdido</Typography>}
              />

              <Divider style={{ margin: "16px 0 8px" }} />
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                Inteligencia Artificial
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.secretariaIaEnabled}
                    onChange={(e) => setLimitsForm({ ...limitsForm, secretariaIaEnabled: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Secretaria IA habilitada (empresa)</Typography>}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                Interruptor de la empresa (nivel tenant). Siempre editable por el superadmin, aunque "El admin puede togglear IA" esté apagado. Si está OFF, el inbound por líneas NO-TurnosIA no se reenvía a n8n. ⚠ Las líneas de TurnosIA (turnosIAEnabled) NO dependen de este switch.
              </Typography>

              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.secretariaIaSuperadminEnabled}
                    onChange={(e) => setLimitsForm({ ...limitsForm, secretariaIaSuperadminEnabled: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">{i18n.t("companies.secretariaIaSuperadminLabel")}</Typography>}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                {i18n.t("companies.secretariaIaSuperadminHelp")}
              </Typography>

              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.vendedorIaSuperadminEnabled}
                    onChange={(e) => setLimitsForm({ ...limitsForm, vendedorIaSuperadminEnabled: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Vendedor IA habilitado</Typography>}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                Si está desactivado, el cliente no puede habilitarlo y el webhook enviará VendedorIA=false siempre.
              </Typography>

              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.iaAdminCanToggle}
                    onChange={(e) => setLimitsForm({ ...limitsForm, iaAdminCanToggle: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">{i18n.t("companies.iaAdminCanToggleLabel")}</Typography>}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                {i18n.t("companies.iaAdminCanToggleHelp")}
              </Typography>

              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={limitsForm.emailReportSuperadminAllowed}
                    onChange={(e) => setLimitsForm({ ...limitsForm, emailReportSuperadminAllowed: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">{i18n.t("settingsExtra.emailReportSuperadminAllowed")}</Typography>}
              />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                {i18n.t("settingsExtra.emailReportSuperadminAllowedHelp")}
              </Typography>

              <Divider style={{ margin: "16px 0 8px" }} />
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                Idioma del sistema
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                Idioma en que el sistema generará mensajes automáticos para los clientes de esta empresa.
              </Typography>
              <Select
                native
                variant="outlined"
                margin="dense"
                value={limitsForm.language}
                onChange={(e) => setLimitsForm({ ...limitsForm, language: e.target.value })}
                style={{ width: 200 }}
              >
                <option value="es">Español</option>
                <option value="it">Italiano</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </Select>

              <Divider style={{ margin: "16px 0 8px" }} />
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                Reapertura de tickets
              </Typography>
              <TextField
                label="Ventana de reapertura (días)"
                margin="dense"
                type="number"
                value={limitsForm.ticketReopenWindowDays}
                onChange={(e) => setLimitsForm({ ...limitsForm, ticketReopenWindowDays: e.target.value })}
                style={{ width: 200 }}
                inputProps={{ min: 0 }}
                helperText="0 = un solo chat continuo sin límite. Este valor es el máximo que puede elegir el cliente."
              />
            </div>
          )}
          {/* ── Tab 4: Carga de Datos ── */}
          {settingsTab === 4 && (
            <div style={{ paddingTop: 16 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
                Formularios embebidos
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 16 }}>
                Hasta 4 formularios externos (n8n, Google Forms, Typeform, etc.) accesibles desde el menú lateral. Solo se muestran a administradores de la empresa.
              </Typography>
              {dataForms.map((form) => (
                <div key={form.slot} style={{ marginBottom: 20, padding: "12px 16px", border: "1px solid #e5e7eb", borderRadius: 8, background: form.enabled ? "#f0fdf4" : "#f9fafb" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      Slot {form.slot}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={form.enabled}
                          onChange={(e) => updateDataFormSlot(form.slot, "enabled", e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Habilitado</Typography>}
                      style={{ margin: 0 }}
                    />
                  </div>
                  <TextField
                    label="Título visible"
                    fullWidth
                    margin="dense"
                    size="small"
                    value={form.title}
                    inputProps={{ maxLength: 100 }}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateDataFormSlot(form.slot, "title", val);
                    }}
                  />
                  <div style={{ marginTop: 4 }}>
                    <Typography variant="caption" color="textSecondary">Tipo de slot</Typography>
                    <Select
                      value={form.slotType || "iframe"}
                      onChange={(e) => updateDataFormSlot(form.slot, "slotType", e.target.value)}
                      style={{ display: "block", minWidth: 200 }}
                    >
                      <MenuItem value="iframe">Iframe (formulario externo)</MenuItem>
                      <MenuItem value="bulk_send">Envío masivo (plantilla Meta)</MenuItem>
                    </Select>
                  </div>
                  {(form.slotType || "iframe") === "iframe" ? (
                    <TextField
                      label="URL del formulario (https://...)"
                      fullWidth
                      margin="dense"
                      size="small"
                      value={form.url}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateDataFormSlot(form.slot, "url", val);
                      }}
                      error={form.url !== "" && !form.url.startsWith("https://")}
                      helperText={form.url !== "" && !form.url.startsWith("https://") ? "La URL debe comenzar con https://" : ""}
                    />
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      <Typography variant="caption" color="textSecondary">Profile de envío masivo</Typography>
                      <Select
                        value={form.profileId || ""}
                        onChange={(e) => updateDataFormSlot(form.slot, "profileId", e.target.value)}
                        style={{ display: "block", minWidth: 260 }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Elegí un profile...</MenuItem>
                        {bulkSendProfilesForSlot.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                      {bulkSendProfilesForSlot.length === 0 && (
                        <Typography variant="caption" color="error" style={{ display: "block", marginTop: 4 }}>
                          No hay profiles activos — creá uno en la pestaña "Envío Masivo".
                        </Typography>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 8, textAlign: "right" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      disabled={!!dataFormSaving[form.slot]}
                      onClick={() => handleSaveDataFormSlot(form.slot)}
                    >
                      {dataFormSaving[form.slot] ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Tab 5: Envío Masivo (feat_210 Fase D) ── */}
          {settingsTab === 5 && (
            <BulkSendProfilesEditor companyId={settingsModal.companyId} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          {settingsTab !== 4 && settingsTab !== 5 && (
            <Button onClick={handleSaveSettings} color="primary" variant="contained">Guardar</Button>
          )}
        </DialogActions>
      </Dialog>
      {/* ── Hard Delete Confirmation Dialog ── */}
      <Dialog
        open={!!hardDeleteTarget}
        onClose={() => !hardDeleting && setHardDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle style={{ color: "#ef4444" }}>Eliminar empresa permanentemente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Esto eliminará <strong>todos los datos</strong> de <strong>{hardDeleteTarget?.name}</strong>: contactos, tickets, mensajes, usuarios, conexiones, colas y configuración.
          </Typography>
          <Typography variant="body2" gutterBottom style={{ color: "#ef4444", fontWeight: 600 }}>
            Esta acción es IRREVERSIBLE.
          </Typography>
          <Typography variant="body2" style={{ marginTop: 12, marginBottom: 4 }}>
            Escribí el nombre de la empresa para confirmar:
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={hardDeleteTarget?.name}
            value={hardDeleteConfirm}
            onChange={e => setHardDeleteConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHardDeleteTarget(null)} disabled={hardDeleting}>Cancelar</Button>
          <Button
            onClick={handleHardDelete}
            variant="contained"
            disabled={hardDeleting || hardDeleteConfirm !== hardDeleteTarget?.name}
            style={{ backgroundColor: hardDeleteConfirm === hardDeleteTarget?.name ? "#ef4444" : undefined, color: hardDeleteConfirm === hardDeleteTarget?.name ? "#fff" : undefined }}
          >
            {hardDeleting ? "Eliminando..." : "Eliminar permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyEditModal;
